// @ts-check
/** @typedef {import('../types/api').DiscountStats} DiscountStats */

import { DollarSign, Edit, Link2, MoreHorizontal, Percent, Plus, Tag, Trash2, TrendingUp, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import DiscountLinkModal from '../components/DiscountLinkModal';
import LoadingScreen from '../components/LoadingScreen';
import BulkActionBar from '../components/ui/BulkActionBar';
import BulkConfirmModal from '../components/ui/BulkConfirmModal';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Modal from '../components/ui/Modal';
import { HeaderCheckbox, RowCheckbox } from '../components/ui/RowCheckbox';
import { useToast } from '../context/ToastContext';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import useRowSelection from '../hooks/useRowSelection';
import adminDiscountService from '../services/adminDiscountService';
import { reportBulkResult, runBulk } from '../services/bulkService';

const DiscountsPage = () => {
  const toast = useToast();
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendDays, setExtendDays] = useState('30');
  const [bulkBusy, setBulkBusy] = useState(false);
  /** @type {[DiscountStats | null, Function]} */
  const [stats, setStats] = useState(null);
  const [editorModal, setEditorModal] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [linkCode, setLinkCode] = useState(/** @type {string | null} */ (null));
  // Touched-since-open flag, reset by openCreateModal/openEditModal (via the
  // raw setter) and after a successful save. Lets X/Cancel confirm before
  // discarding, and lets the shared Modal register with the app-wide guard.
  const [isDirty, setIsDirty] = useState(false);
  const [formData, setFormDataRaw] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 0,
    min_order_amount: 0,
    max_discount_amount: null,
    usage_limit: null,
    applicable_to: 'all',
    applicable_ids: '',
    expires_at: '',
    is_active: true,
  });
  const setFormData = useCallback((updater) => {
    setIsDirty(true);
    setFormDataRaw(updater);
  }, []);

  const fetchDiscounts = useCallback((page, limit) => adminDiscountService.getAllDiscounts({ page, limit }), []);
  const {
    items: discounts,
    loading,
    hasMore,
    reset,
    sentinelRef,
  } = useInfiniteScroll(fetchDiscounts, { limit: 50, deps: [] });

  useEffect(() => {
    const loadStats = async () => {
      try {
        const statsData = await adminDiscountService.getDiscountStats();
        setStats(statsData);
      } catch (error) {
        console.error('Failed to load discount stats:', error);
      }
    };
    loadStats();
  }, []);

  const openCreateModal = () => {
    setFormDataRaw({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 0,
      min_order_amount: 0,
      max_discount_amount: null,
      usage_limit: null,
      applicable_to: 'all',
      applicable_ids: '',
      expires_at: '',
      is_active: true,
    });
    setIsDirty(false);
    setEditorModal('create');
  };

  const openEditModal = (discount) => {
    setFormDataRaw({
      code: discount.code,
      description: discount.description || '',
      discount_type: discount.discount_type,
      discount_value: discount.discount_value,
      min_order_amount: discount.min_order_amount,
      max_discount_amount: discount.max_discount_amount || '',
      usage_limit: discount.usage_limit || '',
      applicable_to: discount.applicable_to,
      applicable_ids: discount.applicable_ids || '',
      expires_at: discount.expires_at || '',
      is_active: discount.is_active,
    });
    setIsDirty(false);
    setEditorModal(discount.id);
  };

  // X icon and footer Cancel both dismiss the editor modal — confirm first
  // if there's anything to lose.
  const handleCloseModal = () => {
    if (isDirty && !window.confirm('You have unsaved changes. Discard them?')) return;
    setEditorModal(null);
  };

  const handleSave = async () => {
    try {
      const submitData = {
        ...formData,
        code: formData.code.toUpperCase(),
        max_discount_amount: formData.max_discount_amount || null,
        usage_limit: formData.usage_limit || null,
        expires_at: formData.expires_at || null,
      };

      if (editorModal === 'create') {
        await adminDiscountService.createDiscount(submitData);
      } else {
        await adminDiscountService.updateDiscount(editorModal, submitData);
      }
      setIsDirty(false);
      setEditorModal(null);
      reset();
    } catch (error) {
      console.error('Failed to save discount:', error);
      alert('Failed to save discount: ' + (error.detail || error.message));
    }
  };

  const handleDelete = async (discountId) => {
    if (!window.confirm('Are you sure you want to delete this discount code?')) return;

    try {
      await adminDiscountService.deleteDiscount(discountId);
      reset();
    } catch (error) {
      console.error('Failed to delete discount:', error);
      alert('Failed to delete discount');
    }
  };

  const selection = useRowSelection(discounts.map((d) => d.id));

  const runBulkAction = async (action, params, verb) => {
    setBulkBusy(true);
    try {
      const result = await runBulk('/api/admin/discounts/bulk', { ids: selection.selectedIds, action, params });
      reportBulkResult(toast, result, verb, 'discount code');
      setExtendOpen(false);
      selection.clear();
      reset();
    } catch (error) {
      toast.error(error.message || 'Bulk action failed');
    } finally {
      setBulkBusy(false);
    }
  };

  if (loading && discounts.length === 0) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-serif">Discount Codes</h1>
          <p className="text-muted-foreground mt-1">Manage promotional discount codes</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center space-x-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90"
        >
          <Plus size={20} />
          <span>New Discount</span>
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-muted-foreground">Total Codes</p>
                <p className="text-2xl font-semibold mt-1">{stats.total_discounts}</p>
              </div>
              <Tag className="text-blue-500" size={32} />
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-muted-foreground">Active Codes</p>
                <p className="text-2xl font-semibold mt-1 text-green-600 dark:text-green-400">
                  {stats.active_discounts}
                </p>
              </div>
              <TrendingUp className="text-green-500" size={32} />
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-muted-foreground">Total Usage</p>
                <p className="text-2xl font-semibold mt-1">{stats.total_usage}</p>
              </div>
              <Percent className="text-purple-500" size={32} />
            </div>
          </div>
        </div>
      )}

      {/* Discounts Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-medium">All Discount Codes</h2>
        </div>

        {discounts.length === 0 ? (
          <div className="p-12 text-center">
            <Tag size={48} className="mx-auto text-muted-foreground/70 mb-4" />
            <h2 className="text-xl font-medium text-foreground mb-2">No Discount Codes</h2>
            <p className="text-muted-foreground max-w-md mx-auto mb-4">
              Create your first discount code to start offering promotions
            </p>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center space-x-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90"
            >
              <Plus size={18} />
              <span>Create Discount</span>
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <BulkActionBar count={selection.count} onClear={selection.clear}>
              <BulkActionBar.Button onClick={() => runBulkAction('activate', {}, 'activated')} disabled={bulkBusy}>
                Activate
              </BulkActionBar.Button>
              <BulkActionBar.Button onClick={() => runBulkAction('deactivate', {}, 'deactivated')} disabled={bulkBusy}>
                Deactivate
              </BulkActionBar.Button>
              <BulkActionBar.Button onClick={() => setExtendOpen(true)} disabled={bulkBusy}>
                Extend expiry
              </BulkActionBar.Button>
            </BulkActionBar>
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="pl-6 py-3">
                    <HeaderCheckbox selection={selection} />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Usage</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Expires</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {discounts.map((discount) => (
                  <tr key={discount.id} className="hover:bg-muted/50">
                    <td className="pl-6 py-4">
                      <RowCheckbox selection={selection} id={discount.id} label={`Select ${discount.code}`} />
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-mono font-bold text-foreground">{discount.code}</div>
                        {discount.description && (
                          <div className="text-sm text-muted-foreground">{discount.description}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center space-x-1 px-2 py-1 bg-muted text-foreground text-xs rounded-md">
                        {discount.discount_type === 'percentage' ? <Percent size={14} /> : <DollarSign size={14} />}
                        <span>{discount.discount_type === 'percentage' ? 'Percentage' : 'Fixed Amount'}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-foreground">
                      {discount.discount_type === 'percentage'
                        ? `${discount.discount_value}%`
                        : `\u20B9${Number(discount.discount_value).toLocaleString('en-IN')}`}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {discount.times_used}
                      {discount.usage_limit && ` / ${discount.usage_limit}`}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md ${
                          discount.is_active
                            ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {discount.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {discount.expires_at ? new Date(discount.expires_at).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="relative inline-block">
                        <button
                          onClick={() => setOpenActionMenu(openActionMenu === discount.id ? null : discount.id)}
                          className="p-1 hover:bg-accent rounded-lg"
                        >
                          <MoreHorizontal size={18} />
                        </button>
                        {openActionMenu === discount.id && (
                          <div className="absolute right-0 mt-1 w-36 bg-card border border-border rounded-lg shadow-lg z-10">
                            <button
                              onClick={() => {
                                openEditModal(discount);
                                setOpenActionMenu(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm hover:bg-muted/50 flex items-center gap-2"
                            >
                              <Edit size={14} />
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                setLinkCode(discount.code);
                                setOpenActionMenu(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm hover:bg-muted/50 flex items-center gap-2"
                            >
                              <Link2 size={14} />
                              Get link
                            </button>
                            <button
                              onClick={() => {
                                handleDelete(discount.id);
                                setOpenActionMenu(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/70 flex items-center gap-2"
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Infinite scroll sentinel */}
      <div ref={sentinelRef} className="flex justify-center py-4">
        {loading && discounts.length > 0 && <LoadingSpinner size="sm" />}
        {!hasMore && discounts.length > 0 && (
          <p className="text-sm text-muted-foreground/70">All discount codes loaded</p>
        )}
      </div>

      {/* Editor Modal */}
      {editorModal && (
        <Modal
          onClose={handleCloseModal}
          maxWidth="2xl"
          className="rounded-lg shadow-xl flex flex-col"
          closeOnBackdrop={false}
          hasUnsavedChanges={isDirty}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h3 className="text-lg font-medium">
              {editorModal === 'create' ? 'Create Discount Code' : 'Edit Discount Code'}
            </h3>
            <button onClick={handleCloseModal} className="text-muted-foreground/70 hover:text-foreground">
              <X size={20} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1">Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-foreground font-mono"
                  placeholder="SUMMER10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1">Type *</label>
                <select
                  value={formData.discount_type}
                  onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-foreground"
                >
                  <option value="percentage">Percentage Off</option>
                  <option value="fixed_amount">Fixed Amount Off</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">Description</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-foreground"
                placeholder="Summer Sale 2024"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1">
                  Value * {formData.discount_type === 'percentage' ? '(%)' : '($)'}
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1">Min Order ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.min_order_amount}
                  onChange={(e) => setFormData({ ...formData, min_order_amount: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-foreground"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1">Usage Limit (Optional)</label>
                <input
                  type="number"
                  min="0"
                  value={formData.usage_limit}
                  onChange={(e) =>
                    setFormData({ ...formData, usage_limit: e.target.value ? parseInt(e.target.value) : '' })
                  }
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-foreground"
                  placeholder="Unlimited"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1">Expiry Date (Optional)</label>
                <input
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  className="w-full px-3 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-foreground"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded-lg border-input focus:ring-2 focus:ring-foreground"
                />
                <span className="text-sm font-medium text-foreground/80">Active</span>
              </label>
            </div>
          </div>

          <div className="px-6 py-4 bg-muted/50 border-t border-border flex justify-end space-x-3">
            <button onClick={handleCloseModal} className="px-4 py-2 border border-input rounded-lg hover:bg-accent">
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
            >
              {editorModal === 'create' ? 'Create' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {linkCode && <DiscountLinkModal code={linkCode} onClose={() => setLinkCode(null)} />}
      {extendOpen && (
        <BulkConfirmModal
          title="Extend expiry"
          message="Pushes each code's expiry date out by this many days. Codes with no expiry date are skipped."
          count={selection.count}
          confirmLabel="Extend"
          busy={bulkBusy}
          onConfirm={() => runBulkAction('extend', { days: Number(extendDays) }, 'extended')}
          onClose={() => setExtendOpen(false)}
        >
          <label className="block text-sm text-foreground/80">
            Days to add
            <input
              type="number"
              min="1"
              max="3650"
              step="1"
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm"
            />
          </label>
        </BulkConfirmModal>
      )}
    </div>
  );
};

export default DiscountsPage;
